/**
 * Plain-English copy and client-side checks for the create-house-and-invite form.
 *
 * Split from the page so the wording and the validation are testable without
 * rendering, and so the error mapping lives in ONE place rather than being
 * re-invented at each call site.
 */

/** Tiers offered in the form. Mirrors the tenants_tier_check constraint. */
export const TIER_OPTIONS = [
  { value: 'complimentary', label: 'Complimentary — comped, full access' },
  { value: 'trial', label: 'Trial' },
  { value: 'solo', label: 'Solo' },
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'pro', label: 'Pro' },
  { value: 'elite', label: 'Elite' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'enterprise_plus', label: 'Enterprise Plus' },
  { value: 'fundraiser', label: 'Fundraiser (vertical)' },
  { value: 'tcg_starter', label: 'TCG Starter (vertical)' },
  { value: 'storage', label: 'Storage (vertical)' },
] as const;

/**
 * The Edge Function's error codes in words a non-engineer can act on.
 *
 * Every one of these says what to DO, because the person reading it is deciding
 * whether to retry, change a field, or call for help — a code like
 * `role_attach_failed` answers none of that.
 */
export function inviteErrorCopy(code: string | null): string {
  switch (code) {
    case null:
      return '';
    case 'missing_name':
      return 'Give the auction house a name.';
    case 'invalid_owner_email':
      return "That email address doesn't look right. Check it and try again.";
    case 'invalid_tier':
      return 'Pick a plan from the list.';
    case 'unusable_slug':
      return 'That name has no letters or numbers in it, so it cannot become a web address. Try a different name.';
    case 'slug_taken':
      return 'Another auction house already uses that web address. Change the name slightly, or set the address yourself.';
    case 'email_already_registered':
      return 'That email already has an AuctiSync account. Use a different address for this house — attaching an existing person to a second house is a separate job.';
    case 'not_super_admin':
      return 'Only a super-admin can create an auction house.';
    case 'unauthenticated':
      return 'Your session expired. Sign in again.';
    // The rest are genuine faults rather than input problems. They all rolled
    // the house back, so the safe advice is identical: nothing was created,
    // try again.
    case 'invite_failed':
      return 'The invitation email could not be sent, so nothing was created. Check the email settings and try again.';
    case 'tenant_create_failed':
    case 'role_attach_failed':
    case 'invite_record_failed':
    case 'slug_check_failed':
      return 'Something went wrong partway through and the house was removed again. Nothing was created. Try once more — if it fails twice, stop and investigate.';
    default:
      return code;
  }
}

/**
 * What an abuse signal MEANS, in words that lead to a decision.
 *
 * `signal_kind: 'domain'` tells a reader nothing about whether to act. These say
 * what was seen and how suspicious it actually is, because two of the three are
 * routinely innocent and treating them all as alarms trains people to ignore
 * the one that isn't.
 */
export function abuseSignalCopy(kind: string, tenantCount: number): string {
  const houses = `${String(tenantCount)} auction houses`;
  switch (kind) {
    case 'email':
      return `The same email address was invited to ${houses}. Often legitimate — one person really can own two — but always worth a look.`;
    case 'domain':
      return `${houses} were opened under the same company email domain. Two branches is ordinary; this many starts to look like one operation.`;
    case 'ip':
      return `${houses} were invited or accepted from the same internet connection. The strongest of the three signals, and the hardest to trigger by accident.`;
    default:
      return `Seen across ${houses}.`;
  }
}

/** Heading for the signal, short enough for a table cell. */
export function abuseSignalLabel(kind: string): string {
  switch (kind) {
    case 'email':
      return 'Same email';
    case 'domain':
      return 'Same company';
    case 'ip':
      return 'Same connection';
    default:
      return kind;
  }
}

/** Mirrors the server check. Deliberately permissive — it only catches typos. */
export function looksLikeEmail(v: string): boolean {
  return /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(v.trim());
}

/**
 * Can the form be submitted?
 *
 * Client-side only, and the server re-checks everything — this exists to stop
 * an obviously-bad submission from sending a real invitation email, not to be
 * the gate.
 */
export function canSubmit(input: { name: string; ownerEmail: string; busy: boolean }): boolean {
  return !input.busy && input.name.trim() !== '' && looksLikeEmail(input.ownerEmail);
}

/**
 * Preview of the web address the house will get.
 *
 * Shown live under the name field because the slug is the tenant's preview
 * subdomain and is awkward to change later — seeing it before submitting is
 * what stops a house being called `dan-s-discount-tools` forever.
 *
 * MUST match `slugify` in the Edge Function. Duplicated across the network
 * boundary rather than shared, since the EF runs on Deno; the test pins the
 * cases that matter, including the apostrophe rule.
 */
export function previewSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’‘`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
}
